// The PandaDoc embedded-signing surface, wrapped once for every flow
// that signs (the pre-need quote flow today; the arrangement flow's
// hand-rolled version is the cautionary tale — ENG-1706).
//
// The SDK's sharpest edge: it REPLACES the container element with its
// iframe, which detaches any React-managed node and corrupts the
// reconciler on the next render. So the container the SDK gets is a DOM
// node React never learns about, created imperatively inside a
// React-managed wrapper — every attempt gets a fresh node, and React
// only ever sees the wrapper.
//
// The first instance pre-initializes on mount, so the iframe shell loads
// while the caller is still preparing documents and minting the session;
// open() fires the moment the session arrives. A LATER session (the next
// signer) rebuilds cleanly — one instance never opens twice. Nothing is
// revealed until the SDK's own `document.loaded` says the document is
// actually painted — strictly after open, never a blank frame.

import {
  useEffect,
  useId,
  useRef,
  useState,
  type MutableRefObject,
} from "react";
import type { Signing } from "pandadoc-signing";

import Spinner from "components/Spinner";

/** One bounded self-recovery per session before the caller hears about
 * it — an exception mid-handshake is usually a stale session or a
 * hiccup, and one clean rebuild resolves it or nothing will. */
const RECOVERY_BUDGET = 1;

const PANDADOC_REGION = "com" as const;

/** Every terminal way the embed can fail to reach a completed sign. The
 * exception path (a live handshake going bad) keeps `embed_error`; the
 * rest are PandaDoc SDK events with no documented payload of their own. */
export type PandaDocEmbedFailureReason =
  | "embed_error"
  | "load_error"
  | "not_found"
  | "protected"
  | "attempts_exceeded";

interface PandaDocEmbedProps {
  /** Null while the caller is still minting — the embed pre-initializes
   * and waits. A NEW session id (the next signer) rebuilds cleanly. */
  sessionId: string | null;
  /** The document actually painted — fires once per session, strictly
   * after open. `documentId` is the PandaDoc document uuid, or null when
   * the event carried none. */
  onLoaded?: (info: { documentId: string | null }) => void;
  /** The signer finished — fired once per session. `documentId` is the
   * PandaDoc document uuid when the completion event carried one. */
  onCompleted: (info: { documentId: string | null }) => void;
  /** The embed gave up on this session (exception after recovery, the
   * SDK failed to load, or one of PandaDoc's own terminal document
   * events). The caller re-mints or shows its error face. */
  onError: (info: {
    reason: PandaDocEmbedFailureReason;
    documentId: string | null;
  }) => void;
  /** Sizes the surface — the iframe fills the wrapper. */
  className?: string;
}

/** The only field of a PandaDoc event payload this embed ever forwards —
 * recipient name/email never leaves this component. */
function readDocumentId(payload: unknown): string | null {
  if (
    payload &&
    typeof payload === "object" &&
    typeof (payload as { uuid?: unknown }).uuid === "string"
  ) {
    return (payload as { uuid: string }).uuid;
  }
  return null;
}

interface EmbedInstance {
  signing: Signing;
  wrapper: HTMLDivElement;
  openedSessionId: string | null;
}

export default function PandaDocEmbed({
  sessionId,
  onLoaded,
  onCompleted,
  onError,
  className,
}: PandaDocEmbedProps) {
  const reactId = useId();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const instanceRef = useRef<EmbedInstance | null>(null);
  const attemptRef = useRef(0);
  const recoveriesRef = useRef(0);
  const unmountedRef = useRef(false);
  const [visible, setVisible] = useState(false);

  // The latest callbacks without re-running the embed lifecycle.
  // The session whose document.loaded has already been reported. A
  // recovery (document.exception with budget left) rebuilds the SDK
  // instance and reopens the SAME session, which fires document.loaded
  // again — the signer saw the document once, so onLoaded fires once.
  const loadedSessionIdRef = useRef<string | null>(null);
  const onLoadedRef = useRef(onLoaded);
  const onCompletedRef = useRef(onCompleted);
  const onErrorRef = useRef(onError);
  onLoadedRef.current = onLoaded;
  onCompletedRef.current = onCompleted;
  onErrorRef.current = onError;

  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      destroyInstance(instanceRef);
    };
  }, []);

  // One lifecycle, three entries: mount with no session (pre-initialize),
  // the first session (open the pre-initialized instance), a later
  // session or a recovery (rebuild, then open). All funnel here.
  useEffect(() => {
    const attempt = async (
      seq: number,
      openSessionId: string | null,
    ): Promise<void> => {
      const wrapper = wrapperRef.current;
      if (!wrapper || unmountedRef.current) return;

      let instance = instanceRef.current;
      const reusable =
        instance !== null &&
        instance.wrapper === wrapper &&
        instance.openedSessionId === null;

      if (!reusable) {
        destroyInstance(instanceRef);
        const containerId = `pandadoc-${reactId.replace(
          /[^a-zA-Z0-9_-]/g,
          "",
        )}-${seq}`;
        const container = document.createElement("div");
        container.id = containerId;
        wrapper.appendChild(container);

        const { Signing } = await import("pandadoc-signing");
        if (unmountedRef.current || attemptRef.current !== seq) return;

        const signing = new Signing(
          containerId,
          {},
          { region: PANDADOC_REGION },
        );
        instance = { signing, wrapper, openedSessionId: null };
        instanceRef.current = instance;

        signing.on("document.loaded", (payload) => {
          if (instanceRef.current?.signing !== signing) return;
          setVisible(true);
          const iframe = wrapper.querySelector("iframe");
          if (iframe) {
            iframe.classList.add("ph-no-capture");
            iframe.style.width = "100%";
            iframe.style.height = "100%";
            iframe.style.border = "0";
            iframe.style.display = "block";
          }
          const opened = instanceRef.current.openedSessionId;
          if (opened !== null && loadedSessionIdRef.current === opened) return;
          loadedSessionIdRef.current = opened;
          onLoadedRef.current?.({ documentId: readDocumentId(payload) });
        });
        signing.on("document.completed", (payload) => {
          if (instanceRef.current?.signing !== signing) return;
          onCompletedRef.current({ documentId: readDocumentId(payload) });
        });
        signing.on("document.exception", (payload) => {
          if (instanceRef.current?.signing !== signing) return;
          const openedId = instanceRef.current.openedSessionId;
          if (!openedId || recoveriesRef.current >= RECOVERY_BUDGET) {
            onErrorRef.current({
              reason: "embed_error",
              documentId: readDocumentId(payload),
            });
            return;
          }
          recoveriesRef.current += 1;
          attemptRef.current += 1;
          setVisible(false);
          void attempt(attemptRef.current, openedId).catch(() => {
            if (!unmountedRef.current) {
              onErrorRef.current({ reason: "embed_error", documentId: null });
            }
          });
        });
        // No payload fields are documented for these four — read only the
        // uuid the same way as the events above, never anything else.
        signing.on("document.loading.error", (payload) => {
          if (instanceRef.current?.signing !== signing) return;
          onErrorRef.current({
            reason: "load_error",
            documentId: readDocumentId(payload),
          });
        });
        signing.on("document.not.found", (payload) => {
          if (instanceRef.current?.signing !== signing) return;
          onErrorRef.current({
            reason: "not_found",
            documentId: readDocumentId(payload),
          });
        });
        signing.on("document.protected", (payload) => {
          if (instanceRef.current?.signing !== signing) return;
          onErrorRef.current({
            reason: "protected",
            documentId: readDocumentId(payload),
          });
        });
        signing.on("document.attempts.limit.exceeded", (payload) => {
          if (instanceRef.current?.signing !== signing) return;
          onErrorRef.current({
            reason: "attempts_exceeded",
            documentId: readDocumentId(payload),
          });
        });
      }

      if (openSessionId && instance) {
        instance.openedSessionId = openSessionId;
        await instance.signing.open({ sessionId: openSessionId });
      }
    };

    attemptRef.current += 1;
    recoveriesRef.current = 0;
    loadedSessionIdRef.current = null;
    if (sessionId) setVisible(false);
    void attempt(attemptRef.current, sessionId).catch(() => {
      if (!unmountedRef.current) {
        onErrorRef.current({ reason: "embed_error", documentId: null });
      }
    });
  }, [sessionId, reactId]);

  return (
    <div className={className}>
      <div className="relative h-full w-full">
        {!visible && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Spinner />
          </div>
        )}
        <div
          ref={wrapperRef}
          className={visible ? "h-full w-full" : "h-full w-full opacity-0"}
          aria-hidden={!visible}
        />
      </div>
    </div>
  );
}

function destroyInstance(
  instanceRef: MutableRefObject<EmbedInstance | null>,
): void {
  const instance = instanceRef.current;
  if (!instance) return;
  instanceRef.current = null;
  try {
    instance.signing.destroy();
  } catch {
    // A dead instance mid-destroy changes nothing — the node goes too.
  }
  instance.wrapper.replaceChildren();
}
